import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { NbDialogRef, NbDialogService, NbTooltipModule } from '@nebular/theme'
import { SharableLinkComponent } from '../sharable-link/sharable-link.component'

interface IPersona {
  id: number
  name: string
  formRegistration: string
  trafficDashboard: string
  embedLink: string
  marketingDashboard: string
  financialDashboard: string
  slug: string
  products_count: number
  enable_payment: boolean
  require_approval: boolean
}

interface IBarcodeClickEvent {
  url: string
  persona: string
}

/**
 * Represents a persona sharable card
 */
@Component({
  selector: 'ngx-sharable-card',
  templateUrl: './sharable-card.component.html',
  styleUrls: ['./sharable-card.component.scss'],
  imports: [CommonModule, SharableLinkComponent, MatIconModule, NbTooltipModule]
})
export class SharableCardComponent implements OnInit {
  @Output() setSelfRegistrationPersona = new EventEmitter<IPersona>()
  @Output() barcodeClickEmitted = new EventEmitter<IBarcodeClickEvent>()
  @Output() selfServicesClickEmitted = new EventEmitter<IPersona>()
  @Output() landingPageClickEmitted = new EventEmitter<IPersona>()

  @Input() persona: IPersona
  /**
   * Event slug
   */
  @Input() eventSlug: string
  /**
   * Event id
   */
  @Input() eventId: number

  /**
   * Public page link
   */
  publicPageLink: string
  /**
   * VCD link
   */
  venueCapacityLink: string
  /**
   * Barcode link
   */
  barcodeLink: string
  /**
   * Landing link
   */
  landingPageLink: string

  /**
   * Self-print app link
   */
  selfPrint: string
  /**
   * A reference to the self service dialoge
   */
  dialogRef: NbDialogRef<any>

  constructor(private auth: AuthService, private dialogService: NbDialogService) {
    this.barcodeClickEmitted = new EventEmitter<{ url: string; persona: string }>()
    this.selfServicesClickEmitted = new EventEmitter<any>()
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    const { slug } = this.auth.currentWorkspace
    if (this.eventId) {
      this.persona.financialDashboard = `${environment.baseUrl}events/${this.eventId}/financial-dashboard`
      this.venueCapacityLink = `${environment.baseUrl}events/${this.eventId}/venue-capacity`
      this.barcodeLink = `${environment.registrationsApi}barcode/link/registration/${slug}/${this.eventSlug}/${this.persona.slug}?type=QR`
    }
    if (this.persona) {
      this.publicPageLink = `${environment.publicPersonaPageUrl}${slug}/${this.eventSlug}/${this.persona.slug}?token=${this.auth.getToken()}`
    }
    if (this.eventId && this.persona) {
      this.landingPageLink = `${environment.baseUrl}event-landing/event/${this.eventId}/form/${this.persona.id}`
    }
  }

  /**
   * Barcode / self-services / landing-page click handler
   */
  handleLinkType(type: 'BARCODE' | 'SELF_SERVICES' | 'LANDING_PAGE') {
    if (type === 'LANDING_PAGE') {
      this.landingPageClickEmitted.emit(this.persona)
      return
    }
    if (this.persona.require_approval && type === 'SELF_SERVICES') return
    this.setSelfRegistrationPersona.emit(this.persona)
    type === 'BARCODE' ? this.barcodeClickEmitted.emit({ url: this.barcodeLink, persona: this.persona.name }) : this.selfServicesClickEmitted.emit(this.persona)
  }

  /**
   * Open the dialog with the template reference passed
   * @param dialogRef TemplateRef<any>
   */
  openSelfServices(dialogRef: TemplateRef<any>) {
    this.dialogRef = this.dialogService.open(dialogRef)
  }

  /**
   * Close the dialog
   */
  closeSelfServices() {
    this.dialogRef.close()
  }
}
